import React, { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { trackSignupConversion } from '../utils/conversionTracking';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Track Google OAuth signups
  useEffect(() => {
    const isNewUser = searchParams.get('newUser');
    
    if (isNewUser === 'true') {
      console.log('🎯 Google OAuth signup detected - tracking conversion');
      trackSignupConversion();
      
      // Clean up URL parameter
      searchParams.delete('newUser');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (!user) return null;

  // Convert bytes to GB (credits are now stored in bytes for perfect precision)
  const creditsInGB = (Math.floor(user.credits / (1024 * 1024 * 1024) * 100) / 100).toFixed(2);
  const totalUsageInGB = user.total_usage_bytes 
    ? (Math.floor((user.total_usage_bytes || 0) / (1024 * 1024 * 1024) * 100) / 100).toFixed(2)
    : '0.00';

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto">
        {/* Compact Header - Matches TopUp/Settings */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white">Dashboard</h1>
            <p className="text-sm text-gray-400">
              Overview of your proxy usage and account stats
            </p>
          </div>
          <a
            href="https://t.me/NetloxProxies"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition-all text-sm whitespace-nowrap"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
            </svg>
            Help
          </a>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <h3 className="text-xs font-medium text-gray-400 mb-2">Remaining Data</h3>
            <p className="text-2xl md:text-3xl font-bold text-blue-400">{creditsInGB} GB</p>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <h3 className="text-xs font-medium text-gray-400 mb-2">Total Usage</h3>
            <p className="text-2xl md:text-3xl font-bold text-green-400">{totalUsageInGB} GB</p>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 sm:col-span-2 lg:col-span-1">
            <h3 className="text-xs font-medium text-gray-400 mb-2">Proxy User ID</h3>
            <p className="text-lg md:text-xl font-bold text-white font-mono break-all">{user.proxyUserId}</p>
          </div>
        </div>

        {/* Account Information */}
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <h2 className="text-base md:text-lg font-semibold text-white mb-3">Account Information</h2>
          <div className="space-y-2">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-4 py-2 border-b border-gray-700">
              <span className="text-xs text-gray-400">Name</span>
              <span className="text-sm text-white font-medium">{user.name}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-4 py-2 border-b border-gray-700">
              <span className="text-xs text-gray-400">Email</span>
              <span className="text-sm text-white font-medium break-all">{user.email}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-4 py-2 border-b border-gray-700">
              <span className="text-xs text-gray-400">Proxy User ID</span>
              <span className="text-sm text-white font-mono font-medium break-all">{user.proxyUserId}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-4 py-2 border-b border-gray-700">
              <span className="text-xs text-gray-400">Remaining Data</span>
              <span className="text-sm text-blue-400 font-semibold">{creditsInGB} GB</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-4 py-2">
              <span className="text-xs text-gray-400">Total Usage</span>
              <span className="text-sm text-green-400 font-semibold">{totalUsageInGB} GB</span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
